import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useMemo, useState } from 'react';
import { pathgen } from 'object-path-generator';
import { Button, Page, PageContentHeader, PageEmptyState, Searchbox, TableList, SortDirection, SortFieldType, useFilterAndSortData } from 'seagull';
import { useStateFromUrl } from 'seagull/utils/useStateFromUrl';
import { CreateProposalButton } from '~/components/CreateProposalButton/CreateProposalButton';
import PageLoadingState from '~/components/PageLoadingState';
import { useSetDocumentTitle } from '~/hooks/useSetDocumentTitle';
import { useLocaleKeys } from '~/i18n';
import { ArchivedProposalRow } from './ArchivedProposalRow/ArchivedProposalRow';
import styles from './ArchivedProposalsDashboard.module.scss';
import { getUICompanyName } from './ArchivedProposalsDashboard.utils';
import ArchivedProposalsEmptyState from './assets/ArchivedProposalsEmptyState.svg';
import { useArchivedProposals } from './hooks/useArchivedProposals';
export const ArchivedProposalsDashboardTestIds = pathgen('ArchivedProposalsDashboardTestIds');
export var ColumnIds = /*#__PURE__*/ function(ColumnIds) {
    ColumnIds[ColumnIds["CompanyName"] = 0] = "CompanyName";
    ColumnIds[ColumnIds["ArchivedBy"] = 1] = "ArchivedBy";
    ColumnIds[ColumnIds["ArchivedDate"] = 2] = "ArchivedDate";
    return ColumnIds;
}({});
function getColumnDefs(columnTexts) {
    return {
        [0]: {
            label: columnTexts.companyName(),
            type: SortFieldType.String,
            getter: 'value',
            getValue: (draft)=>"".concat(getUICompanyName(draft), " ").concat(draft.title)
        },
        [1]: {
            label: columnTexts.archivedBy(),
            type: SortFieldType.String,
            getter: 'value',
            getValue: (draft)=>{
                var _ref, _ref1;
                var _draft_lastModifiedBy, _draft_lastModifiedBy1;
                return "".concat((_ref = (_draft_lastModifiedBy = draft.lastModifiedBy) === null || _draft_lastModifiedBy === void 0 ? void 0 : _draft_lastModifiedBy.firstName) !== null && _ref !== void 0 ? _ref : '', " ").concat((_ref1 = (_draft_lastModifiedBy1 = draft.lastModifiedBy) === null || _draft_lastModifiedBy1 === void 0 ? void 0 : _draft_lastModifiedBy1.lastName) !== null && _ref1 !== void 0 ? _ref1 : '');
            }
        },
        [2]: {
            label: columnTexts.archivedDate(),
            type: SortFieldType.Date,
            getter: 'path',
            path: 'lastModifiedOn'
        }
    };
}
function getTableColumnDefs(columnDefs) {
    return [
        {
            width: [
                180,
                500
            ],
            sortable: true,
            value: columnDefs[0].label,
            bi: {
                name: 'sort_archived_proposals_by_company_name'
            }
        },
        {
            width: [
                120,
                390
            ],
            sortable: true,
            value: columnDefs[1].label,
            bi: {
                name: 'sort_archived_proposals_by_archived_by'
            }
        },
        {
            width: [
                120,
                390
            ],
            sortable: true,
            value: columnDefs[2].label,
            bi: {
                name: 'sort_archived_proposals_by_archived_date'
            }
        }
    ];
}
export const ArchivedProposalsDashboard = ()=>{
    var _ref;
    const { t: texts } = useLocaleKeys('pages.archivedProposalsDashboard');
    const { t: commonTexts } = useLocaleKeys('common');
    const columnDefs = useMemo(()=>getColumnDefs(texts.columns), [
        texts.columns
    ]);
    const TableColumnDefs = useMemo(()=>getTableColumnDefs(columnDefs), [
        columnDefs
    ]);
    const { drafts: rawDrafts, isLoading, isBackgroundLoading } = useArchivedProposals({
        refreshOnHookMount: true
    });
    const [tableSort, setTableSort] = useState({
        colIndex: 2,
        sort: SortDirection.Desc
    });
    const { urlState: { search }, changeUrlState } = useStateFromUrl({
        search: {
            defaultValue: ''
        }
    });
    const filter = useMemo(()=>({
            [0]: {
                search
            }
        }), [
        search
    ]);
    const sort = useMemo(()=>tableSort ? {
            [tableSort.colIndex]: {
                direction: tableSort.sort
            }
        } : undefined, [
        tableSort
    ]);
    const { data: drafts } = useFilterAndSortData({
        data: rawDrafts,
        columnDefs,
        filter,
        sort
    });
    useSetDocumentTitle('Archived proposals');
    if (isLoading) {
        return /*#__PURE__*/ _jsx(PageLoadingState, {});
    }
    const hasDrafts = rawDrafts && rawDrafts.length > 0;
    const onSortChange = (sort)=>{
        setTableSort(sort);
    };
    const onClearSearch = ()=>{
        changeUrlState({
            search: ''
        });
    };
    return /*#__PURE__*/ _jsx(TableList.Provider, {
        hasActionColumn: true,
        columnDefs: TableColumnDefs,
        sort: tableSort,
        onSortChange: onSortChange,
        numRows: (_ref = drafts === null || drafts === void 0 ? void 0 : drafts.length) !== null && _ref !== void 0 ? _ref : 0,
        children: /*#__PURE__*/ _jsx(Page, {
            testId: ArchivedProposalsDashboardTestIds.Root(),
            header: texts.header(),
            actions: /*#__PURE__*/ _jsx(CreateProposalButton, {}),
            loading: isBackgroundLoading,
            children: hasDrafts ? /*#__PURE__*/ _jsxs(_Fragment, {
                children: [
                    /*#__PURE__*/ _jsxs(PageContentHeader, {
                        children: [
                            /*#__PURE__*/ _jsx(Searchbox, {
                                bi: {
                                    name: 'archived_proposals_search'
                                },
                                className: styles.searchbox,
                                placeholder: commonTexts.searchPlaceholder(),
                                registerHotkey: true,
                                testId: ArchivedProposalsDashboardTestIds.Search(),
                                value: search,
                                onChange: (event)=>{
                                    const searchStr = event.target.value;
                                    changeUrlState({
                                        search: searchStr
                                    });
                                },
                                onClear: onClearSearch
                            }),
                            /*#__PURE__*/ _jsx(TableList.Header, {
                                items: TableColumnDefs,
                                testId: ArchivedProposalsDashboardTestIds.TableHeader()
                            })
                        ]
                    }),
                    (drafts === null || drafts === void 0 ? void 0 : drafts.length) ? drafts.map((draft, i)=>/*#__PURE__*/ _jsx(ArchivedProposalRow, {
                            draft: draft,
                            index: i
                        }, draft.id)) : /*#__PURE__*/ _jsx(PageEmptyState, {
                        illustration: /*#__PURE__*/ _jsx(ArchivedProposalsEmptyState, {}),
                        title: texts.noMatchingFilters.title(),
                        message: texts.noMatchingFilters.message(),
                        testId: ArchivedProposalsDashboardTestIds.EmptySearch.Root(),
                        actions: /*#__PURE__*/ _jsx(Button, {
                            bi: {
                                name: 'clear_archived_proposals_search'
                            },
                            testId: ArchivedProposalsDashboardTestIds.EmptySearch.ClearSearch(),
                            onClick: onClearSearch,
                            children: texts.clearSearch()
                        })
                    })
                ]
            }) : /*#__PURE__*/ _jsx(PageEmptyState, {
                testId: ArchivedProposalsDashboardTestIds.EmptyState.Root(),
                illustration: /*#__PURE__*/ _jsx(ArchivedProposalsEmptyState, {}),
                title: texts.emptyState.title(),
                message: texts.emptyState.message(),
                actions: /*#__PURE__*/ _jsx(_Fragment, {
                    children: /*#__PURE__*/ _jsx(CreateProposalButton, {
                        testId: ArchivedProposalsDashboardTestIds.EmptyState.CreateProposalButton(),
                        text: texts.emptyState.createProposal()
                    })
                })
            })
        })
    });
};
